import { SendOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, Input, Popconfirm, Select, Space, Switch, Tag, Typography } from 'antd';
import { useState } from 'react';
import { catalogueApi } from '@/features/catalogue/catalogue.api';
import { contentApi, type ContentItem } from '../content.api';

/**
 * One-off post composer: brief + optional featured product in, an AI-drafted
 * post out — previewed as it will read on the feed, then published with one
 * click (or kept as a draft / discarded). The draft is already persisted by
 * the compose call, so "keep as draft" just closes the preview.
 */
export function ComposePostCard({
  orgId,
  projectId,
  onChanged,
}: {
  orgId: string;
  projectId: string;
  onChanged: () => void;
}) {
  const { message } = App.useApp();
  const [instructions, setInstructions] = useState('');
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const [generateImage, setGenerateImage] = useState(false);
  const [preview, setPreview] = useState<ContentItem | null>(null);

  const products = useQuery({
    queryKey: ['products-all', orgId, projectId] as const,
    queryFn: () => catalogueApi.list(orgId, projectId, 1, 100),
  });

  const compose = useMutation({
    mutationFn: () =>
      contentApi.compose(orgId, projectId, {
        instructions,
        ...(productId ? { productId } : {}),
        generateImage,
      }),
    onSuccess: (item) => {
      setPreview(item);
      onChanged();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not compose the post'),
  });

  const publish = useMutation({
    mutationFn: (id: string) => contentApi.publish(orgId, projectId, id),
    onSuccess: () => {
      message.success('Published to your connected channels');
      reset();
      onChanged();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Publish failed'),
  });

  const discard = useMutation({
    mutationFn: (id: string) => contentApi.remove(orgId, projectId, id),
    onSuccess: () => {
      reset();
      onChanged();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not discard'),
  });

  const reset = () => {
    setPreview(null);
    setInstructions('');
    setProductId(undefined);
    setGenerateImage(false);
  };

  return (
    <Card size="small" title="Compose a post">
      {preview ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card
            style={{ maxWidth: 480 }}
            styles={{ body: { padding: 16 } }}
            cover={
              preview.imageUrl ? (
                <img
                  src={preview.imageUrl}
                  alt={preview.title}
                  style={{ maxHeight: 320, objectFit: 'cover' }}
                />
              ) : undefined
            }
          >
            <Typography.Text strong>{preview.title}</Typography.Text>
            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
              {preview.body}
            </Typography.Paragraph>
            {preview.cta ? <Typography.Paragraph strong>{preview.cta}</Typography.Paragraph> : null}
            <Space wrap size={4}>
              {preview.hashtags.map((h) => (
                <Tag key={h} color="blue">
                  {h.startsWith('#') ? h : `#${h}`}
                </Tag>
              ))}
            </Space>
          </Card>
          <Space wrap>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={publish.isPending}
              onClick={() => publish.mutate(preview.id)}
            >
              Publish now
            </Button>
            <Button onClick={reset}>Keep as draft</Button>
            <Popconfirm title="Discard this post?" onConfirm={() => discard.mutate(preview.id)}>
              <Button danger loading={discard.isPending}>
                Discard
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Input.TextArea
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="What should this post say? e.g. Announce 20% off the hair care bundle this weekend"
          />
          <Space wrap>
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Feature a product (optional)"
              style={{ minWidth: 260 }}
              value={productId ?? null}
              onChange={(v) => setProductId(v ?? undefined)}
              options={(products.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
              loading={products.isLoading}
            />
            <Space size={6}>
              <Switch checked={generateImage} onChange={setGenerateImage} />
              <Typography.Text type="secondary">Generate image</Typography.Text>
            </Space>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={compose.isPending}
              disabled={instructions.trim().length < 3}
              onClick={() => compose.mutate()}
            >
              Generate preview
            </Button>
          </Space>
        </Space>
      )}
    </Card>
  );
}
